import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
  MenuItem,
  Typography,
  Divider,
  Box,
  Card,
  CardMedia,
  IconButton,
  Tooltip
} from '@mui/material';
import { LinkOutlined, Delete } from '@mui/icons-material';
import api from '../../utils/api';
import { getProductImage } from '../../utils/productUtils';

const ProductForm = ({ open, handleClose, productToEdit, onProductSaved }) => {
  const initialFormState = React.useMemo(() => ({
    code: '',
    name: '',
    description: '',
    category: '',
    brand: '',
    purchasePrice: '',
    sellingPrice: '',
    assemblyTime: 0,
    stock: 0,
    minStock: 5,
    location: '',
    isLabor: false,
    doNotRestock: false,
    image: ''
  }), []);

  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [markupPercentage, setMarkupPercentage] = useState(30); // Default markup percentage
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [laborCost, setLaborCost] = useState(30); // Costo orario di default (€/ora)
  const [successMessage, setSuccessMessage] = useState('');
  const [imagePreview, setImagePreview] = useState(getProductImage(productToEdit?.image || ''));
  const [imageError, setImageError] = useState('');

  // Fetch categories and labor cost when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/products/categories/all');
        // Ordina le categorie alfabeticamente
        const sortedCategories = res.data.sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
        setCategories(sortedCategories);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    const fetchLaborCost = async () => {
      try {
        const companyId = localStorage.getItem('companyId');
        if (!companyId) {
          throw new Error('CompanyId not found in localStorage');
        }

        const response = await api.get('/company-settings', {
          headers: {
            'x-company-id': companyId
          }
        });

        if (response.data && response.data.laborHourlyRate) {
          console.log('Tariffa oraria recuperata:', response.data.laborHourlyRate);
          setLaborCost(parseFloat(response.data.laborHourlyRate));
          return parseFloat(response.data.laborHourlyRate);
        }
        
        return 30.00; // Valore di default
      } catch (err) {
        console.error('Error fetching labor cost:', err);
        return 30.00; // Valore di default in caso di errore
      }
    };

    fetchCategories();
    fetchLaborCost();
  }, []);

  // Set form data if editing an existing product
  useEffect(() => {
    if (productToEdit) {
      setFormData({
        ...productToEdit,
        purchasePrice: parseFloat(productToEdit.purchasePrice).toFixed(2),
        sellingPrice: parseFloat(productToEdit.sellingPrice).toFixed(2)
      });

      // Calculate markup percentage
      if (productToEdit.purchasePrice > 0) {
        const markup = ((productToEdit.sellingPrice - productToEdit.purchasePrice) / productToEdit.purchasePrice) * 100;
        setMarkupPercentage(Math.round(markup));
      }
      
      // Aggiorna l'anteprima dell'immagine
      setImagePreview(getProductImage(productToEdit.image || ''));
    } else {
      setFormData(initialFormState);
      setMarkupPercentage(30);
      setImagePreview(getProductImage(''));
      setError(null);
      setSuccessMessage('');
    }
  }, [productToEdit, initialFormState, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue;
    
    // Handle different input types
    if (type === 'checkbox') {
      newValue = checked;
    } else if (type === 'number' || name === 'purchasePrice' || name === 'sellingPrice') {
      newValue = value;
    } else if (name === 'image') {
      // Non convertire gli URL delle immagini in maiuscolo
      newValue = value;
    } else {
      // Convert text inputs to uppercase
      newValue = value.toUpperCase();
    }
    
    // Handle category selection
    if (name === 'category' && value === 'new_category') {
      setOpenCategoryDialog(true);
      return;
    }
    
    setFormData(prev => {
      let updated = { ...prev, [name]: newValue };

      if (name === 'purchasePrice' && !calculatingPrice) {
        const purchasePrice = parseFloat(newValue) || 0;
        updated.sellingPrice = (purchasePrice * (1 + markupPercentage / 100)).toFixed(2);
      }

      if (
        name === 'assemblyTime' ||
        name === 'sellingPrice' ||
        (name === 'purchasePrice' && !calculatingPrice)
      ) {
        const sellingPriceValue = parseFloat(
          (name === 'sellingPrice' || (name === 'purchasePrice' && !calculatingPrice))
            ? updated.sellingPrice
            : prev.sellingPrice
        ) || 0;
        const assemblyTimeValue = parseFloat(
          name === 'assemblyTime' ? newValue : updated.assemblyTime
        ) || 0;
        const priceWithAssembly = sellingPriceValue + (assemblyTimeValue / 60 * laborCost);
        updated.sellingPriceWithAssembly = priceWithAssembly.toFixed(2);
      }

      return updated;
    });

    // Aggiorna l'anteprima dell'immagine se il campo è l'URL dell'immagine
    if (name === 'image') {
      setImagePreview(getProductImage(newValue));
    }

    // Auto-calculate selling price when purchase price changes
    // Update markup percentage when selling price changes
    if (name === 'sellingPrice' && !calculatingPrice && formData.purchasePrice > 0) {
      setCalculatingPrice(true);
      const purchasePrice = parseFloat(formData.purchasePrice) || 0;
      const sellingPrice = parseFloat(value) || 0;
      
      if (purchasePrice > 0) {
        const markup = ((sellingPrice - purchasePrice) / purchasePrice) * 100;
        setMarkupPercentage(Math.round(markup));
      }
      setCalculatingPrice(false);
    }
  };

  const handleAssemblyPriceChange = (value) => {
    setFormData(prev => {
      const sellingPrice = parseFloat(prev.sellingPrice || 0);
      const targetPrice = value === '' ? sellingPrice : parseFloat(value);
      const validTarget = isNaN(targetPrice) ? sellingPrice : targetPrice;
      const laborDifference = Math.max(0, validTarget - sellingPrice);
      const newAssemblyTime = laborCost > 0
        ? (laborDifference / laborCost) * 60
        : prev.assemblyTime;

      return {
        ...prev,
        sellingPriceWithAssembly: value,
        assemblyTime: parseFloat(newAssemblyTime.toFixed(2))
      };
    });
  };

  const handleMarkupChange = (e) => {
    const newMarkup = parseFloat(e.target.value) || 0;
    setMarkupPercentage(newMarkup);
    
    // Update selling price based on new markup
    const purchasePrice = parseFloat(formData.purchasePrice) || 0;
    const newSellingPrice = (purchasePrice * (1 + newMarkup / 100)).toFixed(2);
    setFormData(prev => ({ ...prev, sellingPrice: newSellingPrice }));
  };

  const handleNewCategoryChange = (e) => {
    setNewCategory(e.target.value.toUpperCase());
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    
    try {
      // Add the new category to the server
      await api.post('/products/categories', { category: newCategory });
      
      // Update categories list and sort alphabetically
      const updatedCategories = [...categories, newCategory].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
      setCategories(updatedCategories);
      
      // Set the new category as the selected one
      setFormData({ ...formData, category: newCategory });
      
      // Close the dialog
      setOpenCategoryDialog(false);
      setNewCategory('');
    } catch (err) {
      console.error('Error adding new category:', err);
      // Even if there's an error, we'll add it to the local list and sort
      const updatedCategories = [...categories, newCategory].sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
      setCategories(updatedCategories);
      setFormData({ ...formData, category: newCategory });
      setOpenCategoryDialog(false);
      setNewCategory('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    console.log('Invio dati al server per creare un nuovo prodotto:', formData);

    try {
      // Calculate sellingPriceWithAssembly based on sellingPrice and assemblyTime
      const sellingPriceWithAssembly = (formData.sellingPriceWithAssembly !== undefined && formData.sellingPriceWithAssembly !== '')
        ? parseFloat(formData.sellingPriceWithAssembly)
        : parseFloat(formData.sellingPrice || 0) + (parseFloat(formData.assemblyTime || 0) / 60 * laborCost);

      // Ensure numeric values are properly formatted
      const dataToSubmit = {
        ...formData,
        purchasePrice: parseFloat(formData.purchasePrice) || 0,
        sellingPrice: parseFloat(formData.sellingPrice) || 0,
        price: parseFloat(formData.sellingPrice) || 0, // Aggiungo il campo price come backup
        sellingPriceWithAssembly: parseFloat(sellingPriceWithAssembly.toFixed(2)),
        assemblyTime: parseFloat(formData.assemblyTime) || 0,
        stock: parseInt(formData.stock) || 0,
        minStock: parseInt(formData.minStock) || 5,
        isLabor: Boolean(formData.isLabor),
        doNotRestock: Boolean(formData.doNotRestock)
      };

      let res;
      
      // Se stiamo modificando un prodotto esistente, usiamo PUT
      if (productToEdit) {
        console.log('Aggiornamento prodotto esistente ID:', productToEdit.id);
        console.log('Dati inviati:', JSON.stringify(dataToSubmit, null, 2));
        res = await api.put(`/products/${productToEdit.id}`, dataToSubmit);
      } else {
        // Altrimenti, stiamo creando un nuovo prodotto, usiamo POST
        console.log('Creazione nuovo prodotto');
        res = await api.post('/products', dataToSubmit);
      }
      
      setLoading(false);
      // Reset form data before closing
      setFormData(initialFormState);
      setMarkupPercentage(30);
      setImagePreview(getProductImage(''));
      setError(null);
      setSuccessMessage('');
      
      if (onProductSaved) onProductSaved(res.data);
      handleClose();
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || err.response?.data?.msg || 'Errore durante il salvataggio del prodotto');
      console.error('Error saving product:', err);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image: ''
    }));
    setImagePreview(getProductImage(''));
  };

  // Add this function to calculate the total price with labor
  const calculateTotalPriceWithLabor = () => {
    const sellingPrice = parseFloat(formData.sellingPrice || 0);
    const assemblyTime = parseFloat(formData.assemblyTime || 0);
    return sellingPrice + (assemblyTime / 60 * laborCost);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {productToEdit ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Informazioni Base
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  name="code"
                  label="Codice Prodotto"
                  value={formData.code}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={8}>
                <TextField
                  name="name"
                  label="Nome Prodotto"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  margin="normal"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    image={imagePreview}
                    alt="Anteprima immagine prodotto"
                    sx={{ height: 200, objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.src = getProductImage('');
                    }}
                  />
                  {formData.image && (
                    <IconButton
                      onClick={handleRemoveImage}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                        },
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Card>
                <Box sx={{ mt: 2 }}>
                  <TextField
                    name="image"
                    label="URL Immagine"
                    value={formData.image || ''}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    placeholder="https://esempio.com/immagine.jpg"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkOutlined />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Inserisci l'URL di un'immagine (non carica file per non appesantire il database)"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Descrizione"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  required
                  multiline
                  rows={2}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  name="category"
                  label="Categoria"
                  value={formData.category}
                  onChange={handleChange}
                  fullWidth
                  required
                  select
                  variant="outlined"
                  margin="normal"
                >
                  {categories.length > 0 ? (
                    categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))
                  ) : null}
                  <MenuItem value="new_category" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                    + Aggiungi nuova categoria
                  </MenuItem>
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  name="brand"
                  label="Marca"
                  value={formData.brand}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              
              {/* Pricing Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Informazioni di Prezzo
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  name="purchasePrice"
                  label="Prezzo di Acquisto"
                  value={formData.purchasePrice}
                  onChange={handleChange}
                  fullWidth
                  required
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  name="assemblyTime"
                  label="Tempo di Montaggio (minuti)"
                  value={formData.assemblyTime}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 0.1 }}
                  variant="outlined"
                  margin="normal"
                  helperText="Tempo necessario per il montaggio in minuti"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  name="markupPercentage"
                  label="% Ricarico"
                  value={markupPercentage}
                  onChange={handleMarkupChange}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  name="sellingPrice"
                  label="Prezzo di Vendita"
                  value={formData.sellingPrice}
                  onChange={handleChange}
                  fullWidth
                  required
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  name="sellingPriceWithAssembly"
                  label="Prezzo di Vendita Montaggio Incluso"
                  value={
                    formData.sellingPriceWithAssembly ??
                    (parseFloat(formData.sellingPrice || 0) + (parseFloat(formData.assemblyTime || 0) / 60 * laborCost)).toFixed(2)
                  }
                  onChange={(e) => handleAssemblyPriceChange(e.target.value)}
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                  variant="outlined"
                  margin="normal"
                  helperText="Modifica per variare automaticamente il tempo di montaggio"
                />
              </Grid>
              
              {/* Inventory Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Informazioni di Magazzino
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  name="stock"
                  label="Giacenza Attuale"
                  value={formData.stock}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  name="minStock"
                  label="Scorta Minima"
                  value={formData.minStock}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  name="location"
                  label="Posizione in Magazzino"
                  value={formData.location}
                  onChange={handleChange}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              
              {!formData.isLabor && (
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(formData.doNotRestock)}
                        onChange={(e) => setFormData(prev => ({ ...prev, doNotRestock: e.target.checked }))}
                        name="doNotRestock"
                        color="primary"
                      />
                    }
                    label="Da non riassortire (acquisto una tantum / sostituito)"
                  />
                </Grid>
              )}
              
              {/* Additional Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Informazioni Aggiuntive
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isLabor}
                      onChange={handleChange}
                      name="isLabor"
                      color="primary"
                    />
                  }
                  label="Questo è un servizio di SOLA manodopera"
                />
              </Grid>
              
              {error && (
                <Grid item xs={12}>
                  <Typography color="error">{error}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Annulla
          </Button>
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for adding new category */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Aggiungi Nuova Categoria</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="newCategory"
            label="Nome Categoria"
            type="text"
            fullWidth
            variant="outlined"
            value={newCategory}
            onChange={handleNewCategoryChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)} color="primary">
            Annulla
          </Button>
          <Button onClick={handleAddCategory} color="primary" variant="contained">
            Aggiungi
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductForm;